/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Class of Services module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../config/api';

export const DIAL_RULE_RESTRICTIONS_ENDPOINT = '/api/telephony/dial-rule-restrictions';

export interface DialRuleRestrictionRule {
  id?: number;
  direction: 'outbound' | 'internal';
  pattern: string;
  allowed: boolean;
  announcement?: string | null;
  playMaxDuration: boolean;
  maxDurationSeconds?: number | null;
  requirePassword: boolean;
  priority: number;
  enabled: boolean;
}

export interface DialRuleRestriction {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt?: string;
  rules?: DialRuleRestrictionRule[];
  _count?: {
    rules: number;
  };
}

export interface ApiPagination {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
  pagination?: ApiPagination;
}

export interface ApiErrorResponse {
  success: false;
  message?: string;
  [key: string]: unknown;
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse | null | undefined;

const normaliseHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers.reduce<Record<string, string>>((accumulator, [key, value]) => {
      accumulator[key] = value;
      return accumulator;
    }, {});
  }

  return headers;
};

export async function requestDialRuleRestrictionsApi<T>(
  endpoint: string,
  fallbackMessage: string,
  options: RequestInit = {}
): Promise<ApiSuccessResponse<T>> {
  const response = await fetch(
    buildApiUrl(endpoint),
    {
      ...options,
      headers: {
        ...getApiHeaders(),
        ...normaliseHeaders(options.headers)
      }
    }
  );

  let payload: ApiResponse<T>;

  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (!payload || (typeof payload === 'object' && 'success' in payload && payload.success !== true)) {
    const message = payload && typeof payload === 'object' && 'message' in payload
      ? (payload.message as string | undefined)
      : fallbackMessage;
    throw new Error(message || fallbackMessage);
  }

  if (payload && typeof payload === 'object' && 'success' in payload && payload.success) {
    return payload as ApiSuccessResponse<T>;
  }

  return {
    success: true,
    data: undefined as T
  };
}


